import styled from '@emotion/styled'
import TextareaAutosize from 'react-textarea-autosize'
import { DESIGN_TOKENS } from 'constants/color.constants'
import {
    MouseEventHandler,
    useRef,
    useState,
    forwardRef,
    useImperativeHandle,
} from 'react'
import { pxToRem } from 'utils/rem.utils'
import { ArrowUp, Square } from 'lucide-react'
import Column from 'components/core/Column'
import Row from 'components/core/Row'
import Typography from 'components/core/Typography'
import { useIsMobile } from 'hooks/useIsMobile'
import { FileAttachmentButton } from 'components/composite/FileAttachmentButton'
import { FileAttachmentPill } from 'components/composite/FileAttachmentPill'
import {
    CHAT_ACCEPTED_FILE_TYPES,
    CHAT_MAX_FILE_SIZE_MB,
} from '../wilsonChat.constants'

const StyledWilsonChatPrompt = styled(Column)`
    width: 100%;
    padding: ${pxToRem(12)};
    border: 1px solid ${DESIGN_TOKENS.border.input};
    border-radius: ${pxToRem(14)};
    background-color: ${DESIGN_TOKENS.background.primary};
    cursor: text;

    &::placeholder {
        color: ${DESIGN_TOKENS.text.mutedForeground};
    }

    &:focus-within {
        outline: none;
    }

    .wilsonChatPrompt__textarea {
        width: 100%;
        resize: none;
        background: transparent;
        font-size: ${pxToRem(14)};
        line-height: ${pxToRem(20)};
        color: ${DESIGN_TOKENS.text.foreground};
        font-style: normal;
        font-weight: 400;
        &:focus {
            outline: none;
        }
    }

    .wilsonChatPrompt__controls {
        display: flex;
        justify-content: space-between;

        .wilsonChatPrompt__controls__attachments {
            max-width: ${pxToRem(350)};
        }

        .wilsonChatPrompt__controls__submit {
            border-radius: ${DESIGN_TOKENS.borderRadius.round};
            min-width: ${pxToRem(30)};
            min-height: ${pxToRem(30)};
            display: flex;
            justify-content: center;
            align-items: center;
            background-color: ${DESIGN_TOKENS.background.light};
            transition: ${DESIGN_TOKENS.transition.inputHover};

            &:disabled {
                cursor: not-allowed;
            }

            &:hover {
                background-color: ${DESIGN_TOKENS.background.light};
            }
        }
    }
`

export type WilsonChatPromptRef = {
    focus: () => void
}

export type WilsonChatPromptProps = {
    isHandlingMessage?: boolean
    hasActiveFormWidget?: boolean
    hasAttachments?: boolean
    controls?: React.ReactNode
    placeholder?: string
    footer?: React.ReactNode
    files?: File[]
    onSubmit: (args: { message: string }) => void
    onSetFiles: (files: File[]) => void
}

export const WilsonChatPrompt = forwardRef<
    WilsonChatPromptRef,
    WilsonChatPromptProps
>(
    (
        {
            isHandlingMessage = false,
            hasActiveFormWidget = false,
            hasAttachments = true,
            controls,
            files = [],
            placeholder = 'Ask Wilson anything...',
            onSubmit,
            onSetFiles,
        },
        ref
    ) => {
        const isMobile = useIsMobile()
        const [value, setValue] = useState('')
        const inputRef = useRef<HTMLTextAreaElement>(null)
        const buttonRef = useRef<HTMLButtonElement>(null)

        const _onPromptClick: MouseEventHandler<HTMLDivElement> = (e) => {
            if (!inputRef.current || e.target === buttonRef.current) {
                return
            }
            // Text area is only one part of the whole input. Controls are rendered
            // outside of the text area. If user clicks in the white space around
            // controls we still want to focus the text area.
            inputRef.current.focus()
        }

        const _onSubmit = () => {
            if (isHandlingMessage || hasActiveFormWidget) {
                return
            }
            const valueToSubmit = value.trim()
            if (!valueToSubmit) {
                return
            }
            setValue('')
            onSubmit({ message: valueToSubmit })
        }

        const _onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
            if (e.key === 'Enter' && e.shiftKey) {
                e.preventDefault()
                _onSubmit()
            }
        }

        useImperativeHandle(ref, () => ({
            focus: () => {
                inputRef.current?.focus()
            },
        }))

        const isDisabled = isHandlingMessage || hasActiveFormWidget

        return (
            <StyledWilsonChatPrompt gap="p1" onClick={_onPromptClick}>
                <TextareaAutosize
                    ref={inputRef}
                    disabled={isDisabled}
                    placeholder={
                        !hasActiveFormWidget
                            ? placeholder
                            : 'Please complete the form to proceed'
                    }
                    className="wilsonChatPrompt__textarea"
                    maxRows={10}
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    onKeyDown={_onKeyDown}
                />

                <div className="wilsonChatPrompt__controls">
                    <Row gap="p1">
                        <Row gap="sm">
                            {hasAttachments && (
                                <FileAttachmentButton
                                    maxFileSizeMb={CHAT_MAX_FILE_SIZE_MB}
                                    acceptedFileTypes={CHAT_ACCEPTED_FILE_TYPES}
                                    onFilesSelected={(newFiles) =>
                                        onSetFiles([...files, ...newFiles])
                                    }
                                />
                            )}
                            {controls}
                        </Row>
                        {files.length > 0 && (
                            <Row gap="sm" alignItems="center" flexWrap="wrap">
                                {files.map((file, idx) => (
                                    <FileAttachmentPill
                                        key={idx}
                                        file={file}
                                        onRemove={() =>
                                            onSetFiles(
                                                files.filter((f) => f !== file)
                                            )
                                        }
                                    />
                                ))}
                            </Row>
                        )}
                    </Row>
                    <Row gap="p1" alignItems="center">
                        {!isDisabled && !isMobile && (
                            <Typography
                                isBold
                                noWrap
                                variant="caption"
                                color="secondary"
                                textAlign="end"
                            >
                                Shift + Enter
                            </Typography>
                        )}
                        <button
                            disabled={!value.trim()}
                            ref={buttonRef}
                            className="wilsonChatPrompt__controls__submit"
                            onClick={_onSubmit}
                        >
                            {isDisabled ? (
                                <Square
                                    size={18}
                                    color={DESIGN_TOKENS.text.background}
                                />
                            ) : (
                                <ArrowUp
                                    size={18}
                                    color={DESIGN_TOKENS.text.background}
                                />
                            )}
                        </button>
                    </Row>
                </div>
            </StyledWilsonChatPrompt>
        )
    }
)
